import { getCookie } from 'cookies-next';

export interface ConsoleLogger {
    info(...args: unknown[]): void;
    error(...args: unknown[]): void;
    log(...args: unknown[]): void;
}

/**
 * Checks if a given namespace is enabled in the 'tn_logger' cookie. It will
 * also check for parent namespaces.
 *
 * @param namespace - The namespace string to check against the cookie patterns
 * @returns `true` if the namespace matches any pattern in the cookie, `false` otherwise
 *
 * @example
 * ```typescript
 * // Cookie contains "app,debug:auth"
 * hasNamespaceInCookie('app:user'); // returns true
 * hasNamespaceInCookie('debug:auth:login'); // returns true
 * hasNamespaceInCookie('unknown'); // returns false
 * ```
 */
function hasNamespaceInCookie(namespace: string) {
    const namespaces = getCookie('tn_logger');

    if (typeof namespaces !== 'string') return false;

    return namespaces
        .split(',')
        .map((val) => val.trim().toLowerCase())
        .filter((ns) => ns.length)
        .some((ns) => {
            const regex = new RegExp(`^${ns}:?`);
            return regex.test(namespace.toLowerCase());
        });
}

/**
 * This is a simple logger that logs to the console
 * if the given namespace is in the tn_logger cookie.
 * This cookie should contain a comma separated list of namespaces.
 *
 * @param namespace The namespace preprended to the log message
 * @param enabled This is to enable the logger for tests only
 * @returns
 */
export function createConsoleLogger(namespace: string, enabled = false): ConsoleLogger {
    const isEnabled = () => {
        if (console && (enabled || hasNamespaceInCookie(namespace))) {
            return true;
        }
        return false;
    };

    return {
        info(...args: unknown[]) {
            if (!isEnabled()) return;
            console.info(`${namespace}:`, ...args);
        },
        error(...args: unknown[]) {
            if (!isEnabled()) return;
            console.error(`${namespace}:`, ...args);
        },
        log(...args: unknown[]) {
            if (!isEnabled()) return;
            console.log(`${namespace}:`, ...args);
        },
    };
}

export default createConsoleLogger;
